import { reactive } from 'vue';
import { AxiosError } from 'axios';
import Toastify from 'toastify-js';
import {
    CheckSeatsAvailabilityResponse,
    DeselectSeatRequest, DeselectSeatResponse,
    SelectSeatRequest,
    SelectSeatResponse,
} from '../requestTypes';
import { useFetch } from './useFetch';
import { useLoader } from './useLoader';
import { useReservation } from './useReservation';
import { useLocalTravellers } from './useLocalTravellers';
import { Service } from '../types';

const seatServiceInternalName = 'SERVICE_SEAT_RESERVATION';
const seatServiceObject = window.RESERVATION_STORE.services.find((item) => item.internal_name === seatServiceInternalName) as Service;

const state = reactive<{
    interval: number;
    allSeats: CheckSeatsAvailabilityResponse;
    selectedTraveller: null | string;
}>({
    interval: 0,
    allSeats: [],
    selectedTraveller: null,
});

export const useSeatReservation = () => {
    const { protectedFetch } = useFetch();
    const { showLoader, hideLoader } = useLoader();
    const { reservationState } = useReservation();
    const { localTravellersRef, selectServiceForLocalTraveller, deselectServiceForLocalTraveller } = useLocalTravellers();

    const autoChooseSelectedTraveller = () => {
        const travellerHashes = Object.values(reservationState.value.travellers).map((item) => item.hash);
        const travellersWithoutSeats = travellerHashes.filter((tHash) => !state.allSeats.some((seat) => seat.traveller_hash === tHash));

        state.selectedTraveller = travellersWithoutSeats[0] ?? state.selectedTraveller ?? travellerHashes[0];
    };

    const checkSeats = async () => {
        const response = await protectedFetch<never, CheckSeatsAvailabilityResponse>({
            method: 'get',
            url: '/reservation/seats/check',
        });

        state.allSeats = response.data;

        return response;
    };

    const selectSeat = async (seatNumber: number) => {
        if (!state.selectedTraveller) {
            return null;
        }

        showLoader();

        try {
            const response = await protectedFetch<SelectSeatRequest, SelectSeatResponse>({
                method: 'post',
                url: '/reservation/seats/select',
                data: {
                    seat_number: seatNumber,
                    traveller_hash: state.selectedTraveller,
                },
            });

            const travellerIndex = localTravellersRef.value.findIndex((item) => item.hash === state.selectedTraveller);
            selectServiceForLocalTraveller(travellerIndex, seatServiceObject);

            return response;
        } catch (e) {
            const error = e as AxiosError<{ message: string }>;

            console.error(error.response?.data?.message);

            if (error.response?.status === 403) {
                Toastify({
                    text: error.response?.data?.message,
                    className: 'custom-toast-error',
                    duration: 7000,
                    close: true,
                    gravity: 'bottom',
                    position: 'center',
                    stopOnFocus: true,
                }).showToast();
            }

            return error;
        } finally {
            await checkSeats();
            autoChooseSelectedTraveller();
            hideLoader();
        }
    };

    const deselectSeat = async (seatNumber: number, hash: string) => {
        showLoader();

        try {
            const response = await protectedFetch<DeselectSeatRequest, DeselectSeatResponse>({
                method: 'post',
                url: '/reservation/seats/deselect',
                data: {
                    seat_number: seatNumber,
                },
            });

            const travellerIndex = localTravellersRef.value.findIndex((item) => item.hash === hash);
            deselectServiceForLocalTraveller(travellerIndex, seatServiceInternalName);

            await checkSeats();
            autoChooseSelectedTraveller();

            return response;
        } catch (e) {
            console.error('Seat is already occupied.');
            throw e;
        } finally {
            hideLoader();
        }
    };

    const startCheckSeatsInterval = () => {
        state.interval = setInterval(() => checkSeats(), 6000);
    };

    const stopCheckSeatsInterval = () => {
        clearInterval(state.interval);
    };

    return {
        checkSeats,
        selectSeat,
        deselectSeat,
        startCheckSeatsInterval,
        stopCheckSeatsInterval,
        autoChooseSelectedTraveller,
        seatReservationState: state,
        seatServiceInternalName,
    };
};
